import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { 
  Bars3Icon, 
  XMarkIcon,
  ChevronDownIcon,
  CodeBracketIcon,
  MagnifyingGlassIcon,
  CogIcon,
  ServerIcon,
  CircleStackIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const services = [
    {
      name: 'Web Development',
      href: '/services/web-development',
      icon: CodeBracketIcon,
      description: 'Custom websites & applications'
    },
    {
      name: 'SEO Optimization',
      href: '/services/seo-optimization',
      icon: MagnifyingGlassIcon,
      description: 'Boost your online visibility'
    },
    {
      name: 'IT Consulting',
      href: '/services/it-consulting',
      icon: CogIcon,
      description: 'Strategic technology guidance'
    },
    {
      name: 'System Management',
      href: '/services/system-management',
      icon: ServerIcon,
      description: 'Infrastructure & security'
    },
    {
      name: 'Data Processing',
      href: '/services/data-processing',
      icon: CircleStackIcon,
      description: 'Advanced data solutions'
    },
    {
      name: 'Custom Software',
      href: '/services/custom-software',
      icon: WrenchScrewdriverIcon,
      description: 'Bespoke software development'
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
      // Закрываем выпадающее меню при скролле
      setServicesDropdownOpen(false);
    };

    const handleClickOutside = (event) => {
      if (!event.target.closest('.services-dropdown') && !event.target.closest('[data-services-trigger]')) {
        setServicesDropdownOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Закрываем выпадающее меню при изменении пути
  useEffect(() => {
    setServicesDropdownOpen(false);
    setIsOpen(false);
  }, [location.pathname]);

  const navItems = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '#about' },
    { name: 'Services', href: '/services' },
    { name: 'Contact', href: '#contact' },
  ];

  const handleNavigation = (href) => {
    setIsOpen(false);
    setServicesDropdownOpen(false);
    
    if (href === '/') {
      // Для главной страницы просто переходим на нее
      navigate('/');
    } else if (href.startsWith('#')) {
      // Для якорных ссылок
      if (location.pathname === '/') {
        // Если уже на главной странице, просто скроллим
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // Если на другой странице, переходим на главную и скроллим после загрузки
        navigate('/');
        setTimeout(() => {
          const element = document.querySelector(href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 300);
      }
    } else {
      // Для обычных ссылок на страницы
      navigate(href);
    }
  };

  return (
    <motion.header
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-gray-900/95 backdrop-blur-lg shadow-2xl border-b border-gray-800' 
          : 'bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <motion.div
            className="flex-shrink-0"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <button
              onClick={() => handleNavigation('/')}
              className="text-2xl font-bold bg-gradient-to-r from-green-400 to-green-600 bg-clip-text text-transparent"
            >
              BrightCode
            </button>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="flex items-baseline space-x-4">
              {navItems.map((item, index) => {
                if (item.name === 'Services') {
                  return (
                    <div 
                      key={item.name}
                      className="relative services-dropdown"
                      onMouseEnter={() => setServicesDropdownOpen(true)}
                      onMouseLeave={() => setServicesDropdownOpen(false)}
                    >
                      <motion.button
                        onClick={() => handleNavigation(item.href)}
                        className="text-gray-300 hover:text-green-400 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 relative overflow-hidden group flex items-center"
                        whileHover={{ y: -2 }}
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                      >
                        {item.name}
                        <ChevronDownIcon 
                          className={`w-4 h-4 ml-1 transition-transform duration-200 ${
                            servicesDropdownOpen ? 'rotate-180' : ''
                          }`} 
                        />
                        <span className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-green-400 to-green-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200"></span>
                      </motion.button>
                      
                      {/* Dropdown Menu */}
                      <AnimatePresence>
                        {servicesDropdownOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                            transition={{ duration: 0.2 }}
                            className="absolute top-full left-0 mt-2 w-80 bg-gray-800/95 backdrop-blur-lg rounded-2xl shadow-2xl border border-gray-700 py-4 z-50"
                          >
                            <div className="px-4 pb-2">
                              <h3 className="text-green-400 font-semibold text-sm uppercase tracking-wide">Our Services</h3>
                            </div>
                            <div className="max-h-96 overflow-y-auto">
                              {services.map((service, serviceIndex) => {
                                const IconComponent = service.icon;
                                return (
                                  <Link
                                    key={service.name}
                                    to={service.href}
                                    onClick={() => setServicesDropdownOpen(false)}
                                    className="flex items-start px-4 py-3 hover:bg-green-600/10 transition-colors duration-200 group"
                                  >
                                    <div className="flex-shrink-0 w-10 h-10 bg-green-600/20 rounded-lg flex items-center justify-center mr-3">
                                      <IconComponent className="w-5 h-5 text-green-400" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-white font-medium text-sm group-hover:text-green-400 transition-colors duration-200">
                                        {service.name}
                                      </p>
                                      <p className="text-gray-400 text-xs mt-1">
                                        {service.description}
                                      </p>
                                    </div>
                                  </Link>
                                );
                              })}
                            </div>
                            <div className="px-4 pt-2 mt-2 border-t border-gray-700">
                              <Link
                                to="/services"
                                onClick={() => setServicesDropdownOpen(false)}
                                className="w-full flex items-center justify-center text-green-400 hover:text-green-300 text-sm font-medium py-2 transition-colors duration-200"
                              >
                                View All Services
                              </Link>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }
                
                return (
                  <motion.button
                    key={item.name}
                    onClick={() => handleNavigation(item.href)}
                    className="text-gray-300 hover:text-green-400 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 relative overflow-hidden group"
                    whileHover={{ y: -2 }}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    {item.name}
                    <span className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-green-400 to-green-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200"></span>
                  </motion.button>
                );
              })}
            </div>
          </div>


          {/* Mobile menu button */}
          <div className="md:hidden">
            <motion.button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-300 hover:text-green-400 focus:outline-none"
              whileTap={{ scale: 0.95 }}
            >
              {isOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </motion.button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <motion.div
          className={`md:hidden ${isOpen ? 'block' : 'hidden'}`}
          initial={{ opacity: 0, height: 0 }}
          animate={{ 
            opacity: isOpen ? 1 : 0, 
            height: isOpen ? 'auto' : 0 
          }}
          transition={{ duration: 0.3 }}
        >
          <div className="px-2 pt-2 pb-3 space-y-1 bg-gray-800/95 backdrop-blur-lg rounded-lg mb-4 border border-gray-700">
            {navItems.map((item, index) => {
              if (item.name === 'Services') {
                return (
                  <div key={item.name} className="space-y-1">
                    <motion.button
                      onClick={() => setServicesDropdownOpen(!servicesDropdownOpen)}
                      data-services-trigger
                      className="text-gray-300 hover:text-green-400 flex items-center justify-between w-full px-3 py-2 rounded-md text-base font-medium transition-colors duration-200"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {item.name}
                      <ChevronDownIcon 
                        className={`w-5 h-5 transition-transform duration-200 ${
                          servicesDropdownOpen ? 'rotate-180' : ''
                        }`} 
                      />
                    </motion.button>
                    
                    <AnimatePresence>
                      {servicesDropdownOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden bg-gray-900/50 rounded-lg ml-2 border-l-2 border-green-600/30"
                        >
                          {services.map((service, serviceIndex) => {
                            const IconComponent = service.icon;
                            return (
                              <Link
                                key={service.name}
                                to={service.href}
                                onClick={() => {
                                  setIsOpen(false);
                                  setServicesDropdownOpen(false);
                                }}
                                className="flex items-center px-3 py-2 hover:bg-green-600/10 transition-colors duration-200 group"
                              >
                                <div className="flex-shrink-0 w-8 h-8 bg-green-600/20 rounded-lg flex items-center justify-center mr-3">
                                  <IconComponent className="w-4 h-4 text-green-400" />
                                </div>
                                <div className="flex-1">
                                  <p className="text-gray-300 text-sm group-hover:text-green-400 transition-colors duration-200">
                                    {service.name}
                                  </p>
                                </div>
                              </Link>
                            );
                          })}
                          <Link
                            to="/services"
                            onClick={() => {
                              setIsOpen(false);
                              setServicesDropdownOpen(false);
                            }}
                            className="w-full flex items-center justify-center text-green-400 hover:text-green-300 text-sm font-medium py-2 border-t border-gray-700/50 mt-1 transition-colors duration-200"
                          >
                            View All Services
                          </Link>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }
              
              return (
                <motion.button
                  key={item.name}
                  onClick={() => handleNavigation(item.href)}
                  className="text-gray-300 hover:text-green-400 block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 w-full text-left"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {item.name}
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </nav>
    </motion.header>
  );
};

export default Header;
